import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { formatJobLogEntry, formatShortDateTime } from '../../../utils/jobLogFormatters';
import { panelSurfaceSx } from './controlCenterUtils';

const actionDotColor = (action) => {
  if (!action) return '#94a3b8';
  if (action.includes('invoice') || action.includes('invoiced')) return '#ef4444';
  if (action.includes('status') || action.includes('progress')) return '#f59e0b';
  if (action.includes('technician')) return '#3b82f6';
  if (action.includes('created')) return '#22c55e';
  return '#64748b';
};

export default function RecentActivityPanel({
  data,
  loading,
  error,
  rangeLabel,
  onOpenJob,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const activities = data?.activities || [];

  return (
    <Box sx={{ ...panelSurfaceSx(theme), height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 1.5, sm: 2 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
          Recent Activity
        </Typography>
        {rangeLabel && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{rangeLabel}</Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, maxHeight: 360, overflow: 'auto' }}>
          {activities.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No recent activity
            </Typography>
          ) : (
            activities.map((item) => {
              const title = formatJobLogEntry({
                action: item.action,
                metadata: item.metadata,
              });
              return (
                <Box
                  key={item.id}
                  onClick={() => item.jobId && onOpenJob?.({ id: item.jobId })}
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    py: 1.25,
                    borderTop: '1px solid',
                    borderColor: isDark ? '#334155' : '#e7e7e7',
                    cursor: item.jobId ? 'pointer' : 'default',
                    '&:hover': item.jobId
                      ? { bgcolor: isDark ? '#1e293b' : '#f8fafc' }
                      : undefined,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: actionDotColor(item.action),
                      mt: 0.6,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
                      {title.split('\n')[0]}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                      {[item.clientName, item.address].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                      {formatShortDateTime(item.createdAt)}
                      {item.workOrder ? ` · ${item.workOrder}` : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
}
